import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Search, Printer, Download, Users, Loader2, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

const STATUS = [
  { k: "all",      l: "All" },
  { k: "active",   l: "Active" },
  { k: "inactive", l: "Inactive" },
];

// Rank ordering used for sort — matches the rewards.py RANKS ladder so
// sorting by Rank goes Executive → Sr. Executive → Silver … → Global-Crown
// instead of alphabetical (which would put Crown-Ambassador before Executive).
const RANK_ORDER = [
  "Executive", "Sr. Executive", "Silver", "Gold", "Pearl", "Emerald",
  "Sapphire", "Ruby", "Platinum", "Diamond", "Royal",
  "Crown-Ambassador", "Imperial-Crown", "Global-Crown",
];
const rankIdx = (label) => {
  if (!label) return -1; // "no rank" (—) sorts before Executive when ASC
  const i = RANK_ORDER.indexOf(label);
  return i === -1 ? -1 : i;
};

// Shared with Tree.jsx — kept in sync so a Silver on the tree looks Silver here.
const RANK_CHIP = {
  Executive:          "bg-sky-100 text-sky-700 border-sky-300",
  "Sr. Executive":    "bg-blue-100 text-blue-700 border-blue-300",
  Silver:             "bg-slate-100 text-slate-700 border-slate-300",
  Gold:               "bg-yellow-100 text-yellow-800 border-yellow-300",
  Pearl:              "bg-pink-100 text-pink-700 border-pink-300",
  Emerald:            "bg-emerald-100 text-emerald-700 border-emerald-300",
  Sapphire:           "bg-indigo-100 text-indigo-700 border-indigo-300",
  Ruby:               "bg-rose-100 text-rose-700 border-rose-300",
  Platinum:           "bg-zinc-100 text-zinc-700 border-zinc-300",
  Diamond:            "bg-cyan-100 text-cyan-700 border-cyan-300",
  Royal:              "bg-purple-100 text-purple-700 border-purple-300",
  "Crown-Ambassador": "bg-amber-100 text-amber-800 border-amber-300",
  "Imperial-Crown":   "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300",
  "Global-Crown":     "bg-teal-100 text-teal-700 border-teal-300",
};

// Column config drives both header rendering + sort behaviour. `get` returns
// the value used for comparison (number for numeric cols, lowercased string
// for text cols, RANK_ORDER index for the Rank column).
const COLS = [
  { key: null,               label: "S. No.",          sortable: false },
  { key: "user_code",        label: "User ID",         sortable: true,  type: "text",   get: (u) => (u.user_code || "").toLowerCase() },
  { key: "name",             label: "IBA Name",        sortable: true,  type: "text",   get: (u) => (u.name || "").toLowerCase() },
  { key: "sponsor_code",     label: "Sponsor ID",      sortable: true,  type: "text",   get: (u) => (u.sponsor_code || "").toLowerCase() },
  { key: "direct_referrals", label: "Direct Referral", sortable: true,  type: "number", get: (u) => Number(u.direct_referrals || 0) },
  { key: "left_team",        label: "Left Team",       sortable: true,  type: "number", get: (u) => Number(u.left_team  || 0) },
  { key: "right_team",       label: "Right Team",      sortable: true,  type: "number", get: (u) => Number(u.right_team || 0) },
  { key: "rank_label",       label: "Rank",            sortable: true,  type: "number", get: (u) => rankIdx(u.rank_label) },
  { key: "active",           label: "Status",          sortable: true,  type: "number", get: (u) => (u.active ? 1 : 0) },
];

/** All IBA / All Downline List — the entire recursive placement-tree downline
 *  of the signed-in IBA, presented as a searchable, filterable, printable and
 *  CSV-downloadable table (S.No · User ID · IBA Name · Sponsor ID · Direct
 *  Referral · Left Team · Right Team · Status).
 */
export default function AllDownline() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [q,       setQ]       = useState("");
  const [status,  setStatus]  = useState("all");
  // sort.key === null → natural downline order (BFS from placement engine)
  const [sort,    setSort]    = useState({ key: null, dir: "asc" });

  useEffect(() => {
    let cancelled = false;
    api.get("/dist/all-downline")
      .then((r) => { if (!cancelled) setRows(r.data?.members || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const toggleSort = (key) => {
    setSort((s) => {
      if (s.key !== key)             return { key, dir: "asc" };
      if (s.dir === "asc")           return { key, dir: "desc" };
      return { key: null, dir: "asc" }; // third click → clear sort
    });
  };

  const visible = useMemo(() => {
    const qq = q.trim().toLowerCase();
    const filtered = rows.filter((u) => {
      if (status === "active"   && !u.active) return false;
      if (status === "inactive" && u.active)  return false;
      if (!qq) return true;
      return (
        (u.name         || "").toLowerCase().includes(qq) ||
        (u.user_code    || "").toLowerCase().includes(qq) ||
        (u.sponsor_code || "").toLowerCase().includes(qq)
      );
    });
    if (!sort.key) return filtered;
    const col = COLS.find((c) => c.key === sort.key);
    if (!col?.get) return filtered;
    const dir = sort.dir === "desc" ? -1 : 1;
    return [...filtered].sort((a, b) => {
      const va = col.get(a);
      const vb = col.get(b);
      if (va === vb) return 0;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [rows, q, status, sort]);

  const activeCount = useMemo(() => rows.filter((u) => u.active).length, [rows]);

  const handlePrint = () => window.print();

  const handleDownloadCsv = () => {
    const header = ["S. No.", "User ID", "IBA Name", "Sponsor ID", "Direct Referral", "Left Team", "Right Team", "Rank", "Status"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const body = visible.map((u, i) => [
      i + 1,
      u.user_code,
      u.name,
      u.sponsor_code || "-",
      u.direct_referrals || 0,
      u.left_team  || 0,
      u.right_team || 0,
      u.rank_label || "—",
      u.active ? "Active" : "Inactive",
    ].map(esc).join(","));
    const csv  = [header.map(esc).join(","), ...body].join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `samara-iba-downline-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout
      title="All IBA / Downline List"
      subtitle={loading ? "Loading downline…" : `${visible.length} of ${rows.length} IBAs · ${activeCount} active`}
    >
      {/* Filters + actions */}
      <div className="bg-white border border-border p-4 rounded-sm mb-4 flex flex-col md:flex-row md:items-center gap-3 print:hidden" data-testid="all-downline-filters">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
          <input
            data-testid="all-downline-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, User ID, Sponsor ID…"
            className="w-full border border-border pl-9 pr-3 py-2 text-sm rounded-sm focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/20 transition"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {STATUS.map((f) => (
            <button
              key={f.k}
              type="button"
              onClick={() => setStatus(f.k)}
              data-testid={`all-downline-filter-${f.k}`}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-widest border rounded-sm transition-colors ${
                status === f.k ? "bg-navy text-gold border-navy" : "border-border text-navy hover:border-gold"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>
        <div className="flex gap-2 md:ml-auto">
          <button
            type="button"
            onClick={handlePrint}
            data-testid="all-downline-print"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-widest border border-border text-navy hover:border-gold rounded-sm"
          >
            <Printer size={13} /> Print
          </button>
          <button
            type="button"
            onClick={handleDownloadCsv}
            data-testid="all-downline-download"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-widest bg-navy text-gold hover:bg-navy-deep rounded-sm"
          >
            <Download size={13} /> Download CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-border rounded-sm overflow-x-auto print:border-0" id="all-downline-print-area">
        <div className="hidden print:block px-6 pt-6 pb-3">
          <div className="font-serif text-2xl text-navy">All IBA / Downline List</div>
          <div className="text-sm text-navy/60">SAMARA WIZ · {new Date().toLocaleDateString()} · {visible.length} IBAs</div>
        </div>
        <table className="w-full text-sm min-w-[960px]" data-testid="all-downline-table">
          <thead className="bg-navy text-white print:bg-white print:text-navy print:border-b-2 print:border-navy">
            <tr>
              {COLS.map((c) => {
                if (!c.sortable) {
                  return (
                    <th key={c.label} className="text-left px-4 py-3 text-xs uppercase tracking-widest font-medium">
                      {c.label}
                    </th>
                  );
                }
                const active = sort.key === c.key;
                return (
                  <th key={c.label} className="text-left px-4 py-3 text-xs uppercase tracking-widest font-medium">
                    <button
                      type="button"
                      onClick={() => toggleSort(c.key)}
                      data-testid={`all-downline-sort-${c.key}`}
                      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                      className={`inline-flex items-center gap-1 uppercase tracking-widest font-medium transition-colors print:pointer-events-none print:text-navy ${
                        active ? "text-gold" : "text-white hover:text-gold"
                      }`}
                      title={
                        !active
                          ? `Sort by ${c.label} (ascending)`
                          : sort.dir === "asc"
                            ? `Sort by ${c.label} (descending) — click again to clear`
                            : `Clear ${c.label} sort`
                      }
                    >
                      <span>{c.label}</span>
                      <SortIndicator active={active} dir={sort.dir} />
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="p-8 text-center text-navy/50">
                  <Loader2 size={16} className="inline mr-2 animate-spin" /> Loading downline…
                </td>
              </tr>
            )}
            {!loading && visible.length === 0 && (
              <tr>
                <td colSpan={9} className="p-8 text-center text-navy/50">
                  <Users size={20} className="inline mr-2 opacity-60" />
                  {rows.length === 0 ? "No downline members yet" : "No IBAs match these filters"}
                </td>
              </tr>
            )}
            {!loading && visible.map((u, i) => (
              <tr
                key={u.user_code}
                data-testid={`all-downline-row-${u.user_code}`}
                className="border-b border-border last:border-0 hover:bg-cream/40 print:hover:bg-transparent"
              >
                <td className="px-4 py-3 text-navy/60 font-mono">{i + 1}</td>
                <td className="px-4 py-3 font-medium text-navy font-mono">
                  <Link
                    to={`/tree?root=${u.user_code}`}
                    data-testid={`all-downline-tree-link-${u.user_code}`}
                    className="text-royal hover:text-gold-deep hover:underline underline-offset-2 transition-colors print:text-navy print:no-underline"
                    title={`Open Genealogy tree centered on ${u.user_code}`}
                  >
                    {u.user_code}
                  </Link>
                </td>
                <td className="px-4 py-3 text-navy">{u.name || "—"}</td>
                <td className="px-4 py-3 text-navy/70 font-mono">{u.sponsor_code || "—"}</td>
                <td className="px-4 py-3 text-gold-deep font-medium tabular-nums">{u.direct_referrals || 0}</td>
                <td className="px-4 py-3 text-olive font-medium tabular-nums">{u.left_team  || 0}</td>
                <td className="px-4 py-3 text-rose  font-medium tabular-nums">{u.right_team || 0}</td>
                <td className="px-4 py-3">
                  {u.rank_label ? (
                    <span
                      data-testid={`all-downline-rank-${u.user_code}`}
                      className={`inline-block text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${RANK_CHIP[u.rank_label] || RANK_CHIP.Executive}`}
                    >
                      {u.rank_label}
                    </span>
                  ) : (
                    <span className="text-navy/40 text-xs">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {u.active ? (
                    <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-olive/10 text-olive border border-olive/25">Active</span>
                  ) : (
                    <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-rose/10 text-rose border border-rose/25">Inactive</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <style>{`
        @media print {
          body { background: white !important; }
          @page { size: A4 landscape; margin: 10mm; }
        }
      `}</style>
    </DashboardLayout>
  );
}

function SortIndicator({ active, dir }) {
  if (!active) return <ArrowUpDown size={11} className="opacity-50" />;
  return dir === "asc"
    ? <ArrowUp   size={11} className="text-gold" />
    : <ArrowDown size={11} className="text-gold" />;
}
